// Main recording page - camera preview + record controls

import { useEffect, useRef, useState } from 'react'
import { useRecording } from '@/hooks/useRecording'
import { env } from '@/lib/env'
import CameraPreview from '@/components/CameraPreview'
import RecordButton from '@/components/RecordButton'
import RecordingStatus from '@/components/RecordingStatus'
import LocationToggle from '@/components/LocationToggle'

export default function RecordPage() {
  const {
    videoRef,
    isCameraReady,
    status,
    isRecording,
    chunksUploaded,
    recordingDuration,
    error,
    includeLocation,
    toggleLocation,
    lastVideoId,
    videoFormat,
    initialize,
    startRecording,
    stopRecording,
    reset,
  } = useRecording()

  // Initialize camera on mount (only once)
  const initializedRef = useRef(false)
  useEffect(() => {
    if (!initializedRef.current) {
      initializedRef.current = true
      initialize()
    }
  }, [initialize])

  const handleStartRecording = async () => {
    await startRecording()
  }

  const handleStopRecording = async () => {
    await stopRecording()
  }

  const handleRetry = () => {
    reset()
    initialize()
  }

  const handleRecordAnother = () => {
    reset()
    initialize()
  }

  // Download video to device
  const [saveState, setSaveState] = useState<'idle' | 'preparing' | 'sharing' | 'shared' | 'downloaded'>('idle')

  // Use the recorded video format (mp4 on mobile, webm on desktop)
  const fileExtension = videoFormat || 'webm'
  const mimeType = fileExtension === 'mp4' ? 'video/mp4' : 'video/webm'

  const handleSaveToDevice = async () => {
    if (!lastVideoId || saveState === 'preparing' || saveState === 'sharing') return

    setSaveState('preparing')

    try {
      // Fetch the video file with the correct extension
      const videoUrl = `${env.API_URL}/videos/${lastVideoId}.${fileExtension}`
      const response = await fetch(videoUrl)
      const blob = await response.blob()

      // Create a File object for sharing
      const file = new File([blob], `phillywatch-${lastVideoId.slice(0, 8)}.${fileExtension}`, {
        type: mimeType,
      })

      // Check if Web Share API with files is supported (iOS Safari, Android Chrome)
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        setSaveState('sharing')
        // Use native share sheet
        await navigator.share({
          files: [file],
          title: 'PhillyWatch Video',
        })
        // Share sheet was dismissed (user may have shared or cancelled)
        setSaveState('shared')
      } else {
        // Fallback for desktop: trigger download
        const downloadUrl = `${env.API_URL}/videos/${lastVideoId}.${fileExtension}?download=1`
        const a = document.createElement('a')
        a.href = downloadUrl
        a.style.display = 'none'
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        setSaveState('downloaded')
      }
    } catch (err) {
      // User cancelled share or error occurred
      console.error('Save failed:', err)
      if ((err as Error).name === 'AbortError') {
        // User cancelled - go back to idle so they can try again
        setSaveState('idle')
      } else {
        // Real error - try fallback download
        const downloadUrl = `${env.API_URL}/videos/${lastVideoId}.${fileExtension}?download=1`
        window.location.href = downloadUrl
        setSaveState('downloaded')
      }
    }
  }

  // Show completion state
  if (status === 'complete') {
    return (
      <div className="flex-1 flex flex-col items-center justify-center p-6 bg-gray-900">
        <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mb-6">
          <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
              clipRule="evenodd"
            />
          </svg>
        </div>
        <h2 className="text-2xl font-bold text-white mb-2">Video Saved!</h2>
        <p className="text-gray-400 text-center mb-6">
          Your video has been uploaded and is now under review.
          It will appear in the feed once approved.
        </p>

        {/* Save to Device Button */}
        {lastVideoId && (
          <button
            onClick={handleSaveToDevice}
            disabled={saveState === 'preparing' || saveState === 'sharing'}
            className={`px-6 py-3 mb-4 font-semibold rounded-lg transition-colors flex items-center gap-2 ${
              saveState === 'downloaded'
                ? 'bg-green-600 text-white'
                : 'bg-gray-700 hover:bg-gray-600 text-white disabled:opacity-50'
            }`}
          >
            {saveState === 'preparing' ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Preparing...
              </>
            ) : saveState === 'sharing' ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Tap "Save Video"
              </>
            ) : saveState === 'downloaded' ? (
              <>
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                Downloaded
              </>
            ) : (
              <>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Save to Photos
              </>
            )}
          </button>
        )}

        <button
          onClick={handleRecordAnother}
          className="px-6 py-3 bg-red-500 hover:bg-red-600 text-white font-semibold rounded-lg transition-colors"
        >
          Record Another
        </button>
      </div>
    )
  }

  return (
    <div className="flex-1 flex flex-col bg-black">
      {/* Camera Preview */}
      <div className="flex-1 relative">
        <CameraPreview ref={videoRef} />

        {/* Hero content - shown when ready but not recording */}
        {isCameraReady && !isRecording && status !== 'error' && (
          <div className="absolute top-4 left-4 right-4 z-10 text-center">
            <h1 className="text-xl font-bold text-white drop-shadow-lg">
              Record & Upload Video
            </h1>
            <p className="text-sm text-white/80 mt-1 drop-shadow">
              In Real Time. Anonymously.
            </p>
            <p className="text-sm text-white/60 mt-3 drop-shadow flex items-center justify-center gap-1">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
              </svg>
              Press record below to start
            </p>
          </div>
        )}

        {/* Recording indicator overlay */}
        {isRecording && (
          <div className="absolute top-4 left-4 flex items-center gap-2 bg-black/50 px-3 py-1.5 rounded-full">
            <span className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
            <span className="text-white text-sm font-medium">REC</span>
          </div>
        )}

        {/* Camera not ready overlay */}
        {!isCameraReady && status !== 'error' && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-900/80">
            <div className="text-center">
              <div className="w-12 h-12 border-4 border-gray-600 border-t-white rounded-full animate-spin mx-auto mb-4" />
              <p className="text-gray-400">Initializing camera...</p>
            </div>
          </div>
        )}

        {/* Error overlay */}
        {status === 'error' && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-900/90 p-6">
            <div className="text-center max-w-sm">
              <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Error</h3>
              <p className="text-gray-400 mb-6">{error}</p>
              <button
                onClick={handleRetry}
                className="px-6 py-2 bg-white text-black font-semibold rounded-lg hover:bg-gray-200 transition-colors"
              >
                Try Again
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Controls */}
      <div className="p-6 bg-gray-900 border-t border-gray-800">
        {/* Record Button */}
        <div className="flex justify-center mb-4">
          <RecordButton
            isRecording={isRecording}
            onStart={handleStartRecording}
            onStop={handleStopRecording}
            disabled={!isCameraReady && !isRecording}
          />
        </div>

        {/* Location Toggle */}
        <div className="flex justify-center mb-4">
          <LocationToggle
            checked={includeLocation}
            onChange={toggleLocation}
            disabled={isRecording}
          />
        </div>

        {/* Status */}
        <div className="flex justify-center">
          <RecordingStatus
            status={status}
            chunksUploaded={chunksUploaded}
            duration={recordingDuration}
            error={error}
            onRetry={handleRetry}
          />
        </div>
      </div>
    </div>
  )
}
